// Section « Matchs » de la page équipe (chargée après le reste : la FFBB peut
// être lente) — prochain match, derniers résultats, bilan par compétition,
// classement officiel ; les compétitions de la saison (D-090) : par équipe engagée,
// brassage → championnat → phases finales, coupes à part ; le staff choisit qui
// joue un engagement sans numéro, relie, retire, synchronise.
import { clubSettings, ffbbAlertsMine, linkFfbb, matchChanges, matchPrep, occurrenceRoster, responsesFor, setLinkSquad, teamFfbbLinks, teamMatches, unlinkFfbb, type FfbbLink, type MatchChange, type Squad, type Team, type TeamMatch } from '../lib/api';
import { countdown } from '../domain/match-phase';
import { clubEngagements, pouleStandings, type Engagement } from '../lib/ffbb';
import { summaryText, syncDue, syncTeamFfbb } from '../lib/ffbb-sync';
import { COMPETITION_LABEL, ourStanding, prettyTeamName } from '../domain/ffbb';
import { playedByOpen, pouleLabel, seasonSections } from '../domain/competitions';
import { bilan, outcome } from '../domain/results';
import { localDate } from '../domain/schedule';
import { attendanceCounts, resolveAttendance } from '../domain/attendance';
import { DEFAULT_LEADS } from '../domain/availability';
import { agree } from '../domain/labels';
import { attCtx, leadsFor, loadAvail } from './availability-data';
import { isMinorAccount, refreshContext, squadName, type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast, type Html } from './dom';
import { shortDate, time } from './format';
import { fillCrests, oppCrest } from './crest';

const OUTCOME_LABEL = { win: 'V', loss: 'D', draw: 'N' } as const;

function ago(iso: string | null): string {
  if (!iso) return 'jamais synchronisée';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 2) return 'synchronisée à l\'instant';
  if (min < 60) return `synchronisée il y a ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `synchronisée il y a ${h} h` : `synchronisée le ${new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
}

/** En tête de l'onglet : d'où viennent les matchs et de quand date la dernière lecture FFBB (la plus ancienne).
 *  Sans lien mais avec des matchs venus de la FFBB ou des changements FFBB à valider : jamais « saisis à la main » (D-103). */
function ffbbStrip(links: FfbbLink[], staff: boolean, ffbb: { matches: number; pending: number } = { matches: 0, pending: 0 }): Html {
  if (!links.length && (ffbb.matches || ffbb.pending)) return html`<p class="ms-ffbb small stale"><span>Calendrier FFBB${ffbb.pending
      ? ` : ${ffbb.pending} ${ffbb.pending > 1 ? 'changements' : 'changement'} à valider` : ''}${staff ? ' · relie les compétitions pour suivre les prochains' : ''}.</span>
    ${staff ? html`<button type="button" class="btn btn-link btn-sm" id="ms-link">Relier</button>` : ''}</p>`;
  if (!links.length) return staff ? html`<p class="ms-ffbb small"><span>Pas relié à la FFBB : matchs saisis à la main.</span>
    <button type="button" class="btn btn-link btn-sm" id="ms-link">Relier</button></p>` : html``;
  const never = links.some(l => !l.last_sync_at);
  const oldest = never ? null : links.map(l => l.last_sync_at!).sort()[0]!;
  const stale = never || Date.now() - Date.parse(oldest!) > 24 * 3600_000;
  return html`<p class="ms-ffbb small ${stale ? 'stale' : ''}"><span>Calendrier FFBB : ${never ? 'jamais lu' : `lu ${ago(oldest).replace(/^synchronisée /, '')}`}</span>
    ${staff ? html`<button type="button" class="btn btn-link btn-sm" id="ms-sync">Synchroniser</button>` : ''}</p>`;
}

/**
 * « Compétitions de la saison » (D-090) : par équipe engagée (tout le groupe d'abord : les brassages des
 * jeunes), dans l'ordre de la saison — brassage → championnat → phases finales —, coupes et amicaux à
 * part, avec leur type et leur poule. Le staff (coach, responsable technique, bureau) choisit qui joue un
 * engagement sans numéro (« Joué par »), peut le changer pour les autres, relie, retire, synchronise.
 */
export function seasonCard(links: FfbbLink[], groupSquads: Squad[], staff: boolean): Html {
  const squads = [...groupSquads].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const many = squads.length > 0;
  const item = (l: FfbbLink) => {
    const open = staff && playedByOpen(l, squads.length);
    // La poule ; la date de lecture est en tête de l'onglet (sauf une compétition jamais lue).
    const meta = [pouleLabel(l.poule_name), staff && !l.last_sync_at ? 'jamais lue' : ''].filter(Boolean).join(' · ');
    return html`
      <li data-type="${l.competition_type}">
        <span class="cmp-top"><span class="kind kind-${l.competition_type}">${COMPETITION_LABEL[l.competition_type]}</span>
          <span class="muted small">${meta}</span>
          ${staff ? html`<span class="cmp-acts">${many && !open ? html`<button type="button" class="link-x" data-by-open="${l.engagement_id}">changer</button>` : ''}<button type="button" class="link-x" data-unlink="${l.engagement_id}">retirer</button></span>` : ''}</span>
        <span class="cmp-name">${l.competition_name}</span>
        ${staff && many ? html`<label class="cmp-by" ${open ? '' : 'hidden'}><span>Joué par</span>
          <select data-by="${l.engagement_id}" aria-label="Qui joue ${l.competition_name}">
            <option value="">Tout le groupe</option>
            ${squads.map(q => html`<option value="${q.id}" ${l.squad_id === q.id ? 'selected' : ''}>${q.name}</option>`)}
          </select></label>` : ''}
      </li>`;
  };
  const sections = seasonSections(links, squads).map(s => html`
    <div class="cmp-sec">
      ${many ? html`<div class="cmp-head"><span class="cmp-squad ${s.squad ? '' : 'all'}">${s.squad?.name ?? 'Tout le groupe'}</span>
        ${!s.squad && staff ? html`<span class="muted small">sans numéro FFBB : choisis qui joue</span>` : ''}</div>` : ''}
      ${s.steps.length ? html`<ol class="cmp-steps">${s.steps.map(item)}</ol>` : ''}
      ${s.apart.length ? html`<p class="cmp-apart-label">En parallèle</p><ul class="cmp-apart">${s.apart.map(item)}</ul>` : ''}
      ${s.steps.length || s.apart.length ? '' : html`<p class="muted small">Aucune compétition reliée.</p>`}
    </div>`);
  return html`
    <section class="block cmp">
      <div class="block-head"><h3>Compétitions de la saison</h3>
        ${staff && links.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="b-sync">Synchroniser</button>` : ''}
      </div>
      ${links.length ? sections
        : html`<p class="muted">Relie l'équipe à ses compétitions : matchs, horaires, salles et scores arrivent tout seuls, et l'équipe est prévenue des changements.</p>`}
      ${staff ? html`
        <button type="button" class="btn ${links.length ? 'btn-link' : 'btn-primary'} btn-sm" id="b-link">+ Relier une compétition FFBB</button>
        ${links.length ? html`<p class="hint">Une 2e phase, une coupe ou des phases finales du club se relient toutes seules, une fois par jour, à la première synchro d'un membre du staff.</p>` : ''}` : ''}
    </section>`;
}

export const scoreLine = (m: TeamMatch | { score_us: number | null; score_them: number | null; forfeit: 'us' | 'them' | null; result_confirmed?: boolean }) =>
  m.result_confirmed === false ? ''   // D-113 : un 0-0 FFBB non confirmé ne s'affiche pas comme un score
    : m.forfeit ? (m.forfeit === 'them' ? 'gagné par forfait' : 'perdu par forfait') : m.score_us !== null ? `${m.score_us} – ${m.score_them}` : '';

export async function renderTeamMatches(el: HTMLElement, ctx: Ctx, team: Team, staff: boolean, autoSync = true, squad: string | null = null): Promise<void> {
  const [all, links] = await Promise.all([teamMatches(team.id), teamFfbbLinks(team.id)]);
  // Le groupe engage plusieurs équipes (D-042) : SF1, SF2… une à la fois, ou tout le groupe.
  const squads = ctx.squads.filter(q => q.team_id === team.id);
  const matches = squad ? all.filter(m => m.squad_id === squad) : all;
  const today = localDate(new Date());
  const live = matches.filter(m => !m.events.cancelled_at);
  const played = live.filter(m => outcome({ ...m, cancelled: false }) !== null).reverse();
  const upcoming = live.filter(m => localDate(new Date(m.events.starts_at)) >= today && outcome(m) === null);
  const next = upcoming[0], later = upcoming.slice(1, 5);
  const b = bilan(live.map(m => ({ ...m, cancelled: false })));
  const won = b.reduce((n, x) => n + x.won, 0), lost = b.reduce((n, x) => n + x.lost, 0);
  const form = played.slice(0, 5).reverse();
  const ranked = links.filter(l => l.competition_type !== 'coupe' && l.competition_type !== 'amical' && (!squad || !l.squad_id || l.squad_id === squad));
  // D-117 : un changement venu de la FFBB n'est signalé qu'à ceux qu'il concerne (le staff ; le bureau à domicile) ;
  // les dérogations gardent leurs règles.
  const [allChanges, mineAlerts] = staff ? await Promise.all([matchChanges(live.map(m => m.event_id)).catch(() => [] as MatchChange[]),
    ffbbAlertsMine().catch(() => null)]) : [[] as MatchChange[], null];
  const changes = allChanges.filter(c => c.source !== 'ffbb' || !mineAlerts || mineAlerts.some(a => a.change_id === c.id));
  const changeOf = (id: string) => changes.find(c => c.event_id === id || c.pair_event_id === id);
  const dayOf = (m: TeamMatch) => localDate(new Date(m.events.starts_at));
  const label = (m: TeamMatch) => `${!squad && squadName(ctx, m.squad_id) ? `${squadName(ctx, m.squad_id)} ` : ''}${m.is_home === false ? 'à' : 'contre'} ${m.opponent}`;

  // Le prochain match, côté staff : réponses et composition, d'un coup d'œil.
  let nextChips: Html[] = [];
  if (next) {
    const c = changeOf(next.event_id);
    if (c) nextChips.push(html`<span class="chip chip-warn">${c.source === 'derogation' ? 'Dérogation à décider' : 'FFBB : à valider'}</span>`);
    if (staff) {
      const d = dayOf(next);
      const [roster, resp, prep, avail, leads] = await Promise.all([occurrenceRoster(next.event_id, d).catch(() => []), responsesFor(next.event_id, d).catch(() => []),
        matchPrep(next.event_id).catch(() => null), loadAvail(), leadsFor(team.club_id).catch(() => DEFAULT_LEADS)]);
      // Le même compte que le planning et la fiche du match (D-103, un seul état) : une convoquée est présente par
      // défaut ; pas de « 0/6 réponses » quand le planning dit « 6/6 présentes » (D-121, lot 0).
      const att = resolveAttendance(roster.filter(r => !r.invited).map(r => r.person_id), roster.filter(r => r.invited).map(r => r.person_id), resp,
        attCtx(avail, { kind: 'match' }, { date: d, start: new Date(next.events.starts_at) }, next, leads));
      const n = attendanceCounts(att), g = team.gender;
      nextChips = [...nextChips,
        html`<span class="ms-pill">${n.total ? `${n.present}/${n.total} ${agree(g, 'présent', 'présente', n.present)}` : 'Effectif vide'}</span>`,
        ...(n.uncertain ? [html`<span class="ms-pill">${n.uncertain} ${agree(g, 'incertain', 'incertaine', n.uncertain)}</span>`] : []),
        ...(n.absent ? [html`<span class="ms-pill">${n.absent} ${agree(g, 'absent', 'absente', n.absent)}</span>`] : []),
        html`<span class="ms-pill">${prep?.lineup_announced_at ? 'Compo annoncée' : 'Compo à faire'}</span>`];
    }
  }
  const shown = new Set([next?.event_id, ...later.map(m => m.event_id)]);
  const unseen = changes.filter(c => !shown.has(c.event_id) && !(c.pair_event_id && shown.has(c.pair_event_id)) && live.some(x => x.event_id === c.event_id));

  const resultRow = (m: TeamMatch) => {
    const o = outcome(m)!;
    return html`
      <li><a class="ms-row" href="#/e/${m.event_id}/${dayOf(m)}">
        <span class="ms-o res-${o}">${OUTCOME_LABEL[o]}</span>
        <span class="ms-main"><strong class="crest-line">${oppCrest(m, 'opp-crest-xs')}<span>${label(m)}</span></strong><span class="muted small">${shortDate(dayOf(m))} · ${COMPETITION_LABEL[m.competition]}</span></span>
        <span class="ms-score">${scoreLine(m)}</span>
      </a></li>`;
  };
  const laterRow = (m: TeamMatch) => {
    const c = changeOf(m.event_id);
    const d = new Date(m.events.starts_at);
    return html`
      <li><a class="ms-row" href="#/e/${m.event_id}/${dayOf(m)}">
        <span class="ms-date"><b>${d.getDate()}</b><small>${d.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</small></span>
        <span class="ms-main"><strong class="crest-line">${oppCrest(m, 'opp-crest-xs')}<span>${label(m)}</span></strong><span class="muted small">${COMPETITION_LABEL[m.competition]} · ${time(d)}${m.is_home === false ? ' · extérieur' : m.is_home ? ' · domicile' : ''}</span></span>
        ${c ? html`<span class="chip chip-warn">${c.kind === 'swap' ? 'Inversion' : c.source === 'derogation' ? 'Dérogation' : 'À valider'}</span>` : ''}
      </a></li>`;
  };

  mount(el, html`
    ${squads.length > 1 ? html`<nav class="ms-squads" aria-label="Équipe engagée">
      <button type="button" class="team-pill ${squad ? '' : 'on'}" data-squad="">Tout le groupe</button>
      ${squads.map(q => html`<button type="button" class="team-pill ${squad === q.id ? 'on' : ''}" data-squad="${q.id}">${q.name}</button>`)}</nav>` : ''}
    ${isMinorAccount(ctx) ? '' : ffbbStrip(links, staff, { matches: live.filter(m => m.ffbb_match_id).length, pending: changes.filter(c => c.source === 'ffbb').length })}
    ${played.length || ranked.length ? html`
      <section class="block ms-sum">
        <div class="ms-rank" id="ms-rank" aria-live="polite"></div>
        <div class="ms-bilan"><strong>${won} ${won > 1 ? 'victoires' : 'victoire'} · ${lost} ${lost > 1 ? 'défaites' : 'défaite'}</strong>
          ${form.length ? html`<div class="ms-form" aria-label="Les ${form.length} derniers matchs">${form.map(m => { const o = outcome(m)!; return html`<span class="ms-o res-${o}">${OUTCOME_LABEL[o]}</span>`; })}</div>` : ''}</div>
        ${ranked.length ? html`<button type="button" class="btn btn-link btn-sm" id="ms-go-rank">Classement</button>` : ''}
      </section>` : ''}
    ${next ? html`
      <a class="ms-next" href="#/e/${next.event_id}/${dayOf(next)}">
        <span class="ms-next-top"><span>PROCHAIN${countdown(dayOf(next), today) ? ` · ${countdown(dayOf(next), today)!.toUpperCase()}` : ''}</span><span>${next.is_home === false ? 'EXTÉRIEUR' : next.is_home ? 'DOMICILE' : ''}</span></span>
        <span class="ms-next-vs">${oppCrest(next, 'opp-crest-md mh-on-dark')}<strong>${label(next).toUpperCase()}</strong></span>
        <span class="ms-next-when">${shortDate(dayOf(next)).toUpperCase()} · ${time(new Date(next.events.starts_at)).toUpperCase()}${next.events.location ? ` · ${next.events.location.toUpperCase()}` : ''}</span>
        ${nextChips.length ? html`<span class="ms-chips">${nextChips}</span>` : ''}
      </a>` : html`<p class="muted">Pas de match à venir${links.length ? '' : ' : relie l’équipe à ses compétitions FFBB, ou ajoute un match au planning'}.</p>`}
    ${unseen.length ? html`
      <section class="block mx-alert">
        <span class="eyebrow">À valider · changements de match</span>
        <ul class="res-list">${unseen.map(c => { const m = live.find(x => x.event_id === c.event_id)!; return html`
          <li class="res-row res-chg"><a href="#/e/${c.event_id}/${dayOf(m)}">
            <span class="res-main"><strong>${m.opponent}</strong><span class="muted small">${c.source === 'derogation' ? 'dérogation · ' : ''}${c.kind === 'removed' ? 'retiré du calendrier FFBB' : `${shortDate(dayOf(m))} → ${c.new_starts_at ? `${shortDate(localDate(new Date(c.new_starts_at)))} à ${time(new Date(c.new_starts_at))}` : ''}`}</span></span>
            <span class="chip chip-warn">À valider</span></a></li>`; })}</ul>
      </section>` : ''}
    ${later.length ? html`<section class="block ms-list"><h3>À venir</h3><ul>${later.map(laterRow)}</ul></section>` : ''}
    ${played.length ? html`<section class="block ms-list"><h3>Résultats</h3><ul>${played.slice(0, 6).map(resultRow)}</ul></section>` : ''}

    ${ranked.map(l => html`
      <section class="block" data-standings="${l.poule_id}" data-engagement="${l.engagement_id}">
        <h3>Classement · ${l.competition_name}</h3>
        <p class="muted small">Chargement du classement officiel FFBB…</p>
      </section>`)}

    ${staff || links.length ? seasonCard(links, squads, staff) : ''}`);

  void fillCrests(el);
  for (const bq of $$<HTMLButtonElement>(el, '[data-squad]')) bq.addEventListener('click', () => void renderTeamMatches(el, ctx, team, staff, false, bq.dataset.squad || null));
  $(el, '#ms-go-rank')?.addEventListener('click', () => $(el, '[data-standings]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));

  // Classements officiels, en direct (données publiques FFBB).
  for (const sec of $$(el, '[data-standings]')) {
    pouleStandings(sec.dataset.standings!).then(rows => {
      const ours = ourStanding(rows, sec.dataset.engagement);
      const rank = $(el, '#ms-rank');
      if (ours && rank && !rank.childElementCount) mount(rank, html`<b>${ours.pos}<sup>e</sup></b><small>sur ${rows.length}</small>`);
      mount(sec, html`
        <h3>Classement · ${links.find(l => l.poule_id === sec.dataset.standings)?.competition_name ?? ''}</h3>
        ${rows.length ? html`<div class="table-wrap"><table class="standings">
          <thead><tr><th>#</th><th class="st-team">Équipe</th><th>J</th><th>V</th><th>D</th><th>Pts</th></tr></thead>
          <tbody>${rows.map(r => html`<tr class="${r === ours ? 'st-ours' : ''}"><td>${r.pos}</td><td class="st-team">${r.team}</td><td>${r.played}</td><td>${r.wins}</td><td>${r.losses}</td><td><strong>${r.pts}</strong></td></tr>`)}</tbody>
        </table></div><p class="muted small">Classement officiel FFBB.</p>` : html`<p class="muted">Pas encore de classement.</p>`}`);
    }).catch(() => mount(sec, html`<h3>Classement</h3><p class="muted">FFBB injoignable pour l'instant.</p>`));
  }

  if (!staff) return;
  const reload = () => renderTeamMatches(el, ctx, team, staff, false, squad);
  const runSync = async (only?: string, quiet = false) => {
    const btn = $<HTMLButtonElement>(el, '#b-sync');
    if (btn) { btn.disabled = true; btn.textContent = 'Synchronisation…'; }
    // Une fois par jour, la synchro relit aussi les engagements du club (2e phase, coupe, phases finales).
    const s = await syncTeamFfbb(team.id, only, team.club_id);
    if (!quiet || s.inserted || s.updated || s.toValidate || s.failed.length || s.linked.length) toast(summaryText(s), s.failed.length ? 'ko' : 'ok');
    // Équipes engagées ou équipe créées par la détection : le contexte les apprend.
    if (s.linked.length || s.newTeams.length) await refreshContext(ctx).catch(() => undefined);
    await reload();
  };
  $(el, '#b-sync')?.addEventListener('click', () => runSync());
  $(el, '#ms-sync')?.addEventListener('click', () => runSync());
  $(el, '#ms-link')?.addEventListener('click', () => $(el, '#b-link')?.click());
  for (const b of $$<HTMLButtonElement>(el, '[data-unlink]')) {
    confirmTwice(b, 'confirmer (les matchs restent)', async () => { await unlinkFfbb(team.id, b.dataset.unlink!); await reload(); });
  }
  // « Joué par » : l'équipe engagée qui joue cette compétition (ou tout le groupe) ; ses matchs suivent.
  for (const b of $$<HTMLButtonElement>(el, '[data-by-open]')) b.addEventListener('click', () => {
    const by = b.closest('li')?.querySelector<HTMLElement>('.cmp-by');
    if (by) { by.hidden = false; b.remove(); by.querySelector('select')?.focus(); }
  });
  for (const sel of $$<HTMLSelectElement>(el, '[data-by]')) sel.addEventListener('change', async () => {
    const id = sel.value || null;
    sel.disabled = true;
    try {
      const n = await setLinkSquad(team.id, sel.dataset.by!, id);
      const who = id ? squads.find(q => q.id === id)?.name ?? '' : 'tout le groupe';
      toast(`Joué par ${who}${n ? ` : ${n} match${n > 1 ? 's' : ''} mis à jour` : ''}`);
    } catch { toast('Impossible pour le moment.', 'ko'); }
    await reload();
  });
  $(el, '#b-link')?.addEventListener('click', () => openLinkSheet(ctx, team, links, async (engagementId) => { await runSync(engagementId); }));
  if (autoSync && links.length && syncDue(links)) void runSync(undefined, true);
}

async function openLinkSheet(ctx: Ctx, team: Team, links: FfbbLink[], onLinked: (engagementId: string) => Promise<void>) {
  const club = await clubSettings(team.club_id);
  const isAdmin = ctx.adminOf.some(c => c.id === team.club_id);
  if (!club?.ffbb_club_code) {
    openSheet('Relier à la FFBB', html`
      <p>Il faut d'abord le <strong>code FFBB du club</strong> (ex. OCC0032016), visible sur la fiche du club sur ffbb.com.</p>
      ${isAdmin ? html`<a class="btn btn-primary" href="#/club/equipes" data-close>Le renseigner : Club › Équipes et coachs</a>` : html`<p class="muted">C'est le bureau qui le renseigne (Club › Équipes et coachs).</p>`}`);
    return;
  }
  let engs: Engagement[];
  try { engs = await clubEngagements(club.ffbb_club_code); }
  catch { toast('FFBB injoignable pour l\'instant.', 'ko'); return; }
  const linked = new Set(links.map(l => l.engagement_id));
  const sex = team.gender === 'M' ? 'M' : team.gender === 'F' ? 'F' : null;
  const list = engs.filter(e => !linked.has(e.engagement_id))
    .sort((a, b) => Number(b.sex === sex) - Number(a.sex === sex) || (a.category ?? '').localeCompare(b.category ?? '') || a.competition_name.localeCompare(b.competition_name));
  openSheet('Relier une compétition', html`
    <input class="search" type="search" placeholder="Chercher (U15, seniors, trophée…)" aria-label="Chercher">
    <p class="hint">Engagements de ${club.name} sur la FFBB. Choisis ceux de <strong>${team.name}</strong> : championnat, coupes, brassages.</p>
    <ul class="pick-list">${list.map(e => html`
      <li><button type="button" data-eng="${e.engagement_id}" data-q="${`${e.competition_name} ${e.category ?? ''} ${e.team_name ?? ''}`.toLowerCase()}">
        <span class="pick-main"><strong>${e.competition_name}</strong>
          <span class="muted small">${e.category ?? ''}${e.sex ? ` · ${e.sex === 'F' ? 'féminin' : 'masculin'}` : ''} · ${e.poule_name}${e.team_name ? ` · ${prettyTeamName(e.team_name)}` : ''}${e.team_number ? ` (équipe ${e.team_number})` : ''}</span></span>
        <span class="kind kind-${e.competition_type}">${COMPETITION_LABEL[e.competition_type]}</span>
      </button></li>`)}</ul>
    ${list.length ? '' : html`<p class="muted">Toutes les compétitions du club sont déjà reliées.</p>`}`, (sheet, close) => {
    const search = $<HTMLInputElement>(sheet, '.search')!;
    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      $$(sheet, '[data-eng]').forEach(b => { b.parentElement!.hidden = !!q && !b.dataset.q!.includes(q); });
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-eng]')) {
      b.addEventListener('click', async () => {
        const e = list.find(x => x.engagement_id === b.dataset.eng)!;
        try {
          // Avec son numéro FFBB, la compétition rejoint toute seule l'équipe engagée de ce numéro (D-090).
          await linkFfbb({
            team_id: team.id, engagement_id: e.engagement_id, poule_id: e.poule_id, competition_name: e.competition_name,
            competition_code: e.competition_code, competition_type: e.competition_type, ffbb_team_name: e.team_name,
            team_number: e.team_number, poule_name: e.poule_name || null,
          });
          close();
          await onLinked(e.engagement_id);
        } catch { toast('Liaison impossible.', 'ko'); }
      });
    }
  });
}
